import { useEffect, useRef, useCallback, useState } from "react";
import { trpc } from "@/lib/trpc";

// ========== 通知事件类型定义 ==========
export type NotificationEventType =
  | "trade_buy"
  | "trade_sell"
  | "stop_loss_pause"
  | "daily_loss_pause"
  | "engine_pause"
  | "engine_stop"
  | "slippage_protect"
  | "tail_protect"
  | "engine_start"
  | "llm_limit";

// 事件类型配置
const EVENT_CONFIG: Record<string, {
  label: string;
  sound: "buy" | "sell" | "warning" | "alert" | "info";
  priority: "high" | "medium" | "low";
  defaultEnabled: boolean;
}> = {
  trade_buy: { label: "买入信号", sound: "buy", priority: "high", defaultEnabled: true },
  trade_sell: { label: "卖出信号", sound: "sell", priority: "high", defaultEnabled: true },
  stop_loss_pause: { label: "连续止损暂停", sound: "alert", priority: "high", defaultEnabled: true },
  daily_loss_pause: { label: "单日亏损暂停", sound: "alert", priority: "high", defaultEnabled: true },
  engine_pause: { label: "引擎暂停", sound: "warning", priority: "medium", defaultEnabled: true },
  engine_stop: { label: "引擎停止", sound: "warning", priority: "medium", defaultEnabled: true },
  slippage_protect: { label: "滑点保护", sound: "warning", priority: "medium", defaultEnabled: true },
  tail_protect: { label: "尾盘保护", sound: "info", priority: "low", defaultEnabled: true },
  engine_start: { label: "引擎启动", sound: "info", priority: "low", defaultEnabled: false },
  llm_limit: { label: "LLM限流", sound: "info", priority: "low", defaultEnabled: false },
};

export { EVENT_CONFIG };

// ========== 通知设置类型 ==========
export interface NotificationSettings {
  enabled: boolean;
  soundEnabled: boolean;
  volume: number; // 0-100
  browserNotification: boolean;
  enabledEvents: Record<string, boolean>;
}

const DEFAULT_SETTINGS: NotificationSettings = {
  enabled: true,
  soundEnabled: true,
  volume: 70,
  browserNotification: true,
  enabledEvents: Object.fromEntries(
    Object.entries(EVENT_CONFIG).map(([k, v]) => [k, v.defaultEnabled])
  ),
};

// ========== 本地存储 ==========
const STORAGE_KEY = "quant_notification_settings";

function loadSettings(): NotificationSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch {}
  return DEFAULT_SETTINGS;
}

function saveSettings(settings: NotificationSettings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {}
}

// ========== Web Audio API 提示音生成 ==========
function createAudioContext(): AudioContext | null {
  try {
    return new (window.AudioContext || (window as any).webkitAudioContext)();
  } catch {
    return null;
  }
}

function playTone(ctx: AudioContext, type: "buy" | "sell" | "warning" | "alert" | "info", volume: number) {
  const gain = ctx.createGain();
  gain.connect(ctx.destination);
  gain.gain.value = volume / 100 * 0.3; // 最大30%音量

  const now = ctx.currentTime;

  switch (type) {
    case "buy": {
      // 上升音调 - 积极
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(523, now); // C5
      osc.frequency.linearRampToValueAtTime(784, now + 0.15); // G5
      osc.connect(gain);
      gain.gain.setValueAtTime(volume / 100 * 0.3, now);
      gain.gain.linearRampToValueAtTime(0, now + 0.3);
      osc.start(now);
      osc.stop(now + 0.3);
      break;
    }
    case "sell": {
      // 下降音调
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(784, now); // G5
      osc.frequency.linearRampToValueAtTime(523, now + 0.15); // C5
      osc.connect(gain);
      gain.gain.setValueAtTime(volume / 100 * 0.3, now);
      gain.gain.linearRampToValueAtTime(0, now + 0.3);
      osc.start(now);
      osc.stop(now + 0.3);
      break;
    }
    case "warning": {
      // 双音提示
      const osc1 = ctx.createOscillator();
      osc1.type = "triangle";
      osc1.frequency.value = 440; // A4
      osc1.connect(gain);
      gain.gain.setValueAtTime(volume / 100 * 0.25, now);
      gain.gain.setValueAtTime(0, now + 0.15);
      gain.gain.setValueAtTime(volume / 100 * 0.25, now + 0.2);
      gain.gain.linearRampToValueAtTime(0, now + 0.4);
      osc1.start(now);
      osc1.stop(now + 0.4);
      break;
    }
    case "alert": {
      // 紧急三连音
      for (let i = 0; i < 3; i++) {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = "square";
        osc.frequency.value = 880; // A5
        osc.connect(g);
        g.connect(ctx.destination);
        g.gain.setValueAtTime(volume / 100 * 0.15, now + i * 0.15);
        g.gain.linearRampToValueAtTime(0, now + i * 0.15 + 0.1);
        osc.start(now + i * 0.15);
        osc.stop(now + i * 0.15 + 0.1);
      }
      break;
    }
    case "info": {
      // 轻柔提示
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = 659; // E5
      osc.connect(gain);
      gain.gain.setValueAtTime(volume / 100 * 0.2, now);
      gain.gain.linearRampToValueAtTime(0, now + 0.2);
      osc.start(now);
      osc.stop(now + 0.2);
      break;
    }
  }
}

// ========== 浏览器通知 ==========
function showBrowserNotification(title: string, body: string, tag?: string) {
  if (!("Notification" in window)) return;
  if (Notification.permission !== "granted") return;

  try {
    new Notification(title, {
      body,
      tag: tag || `quant_${Date.now()}`,
      icon: "/favicon.ico",
      silent: true, // 我们自己播放声音
    });
  } catch {}
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (!("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  const result = await Notification.requestPermission();
  return result === "granted";
}

// ========== 主Hook ==========
export function useNotificationSound() {
  const [settings, setSettings] = useState<NotificationSettings>(loadSettings);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const lastCheckTimeRef = useRef<string>(new Date().toISOString());
  const processedIdsRef = useRef<Set<number>>(new Set());

  // 获取最新通知
  const { data: latestNotifications } = trpc.notifications.latest.useQuery(
    { since: lastCheckTimeRef.current },
    {
      enabled: settings.enabled,
      refetchInterval: 5000, // 每5秒检查一次
    }
  );

  // 未读计数
  const { data: unreadData } = trpc.notifications.unreadCount.useQuery(
    undefined,
    {
      enabled: settings.enabled,
      refetchInterval: 10000,
    }
  );

  // 初始化AudioContext
  const initAudio = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = createAudioContext();
    }
    if (audioCtxRef.current?.state === "suspended") {
      audioCtxRef.current.resume();
    }
  }, []);

  // 处理新通知
  useEffect(() => {
    if (!latestNotifications || !settings.enabled) return;

    for (const notif of latestNotifications) {
      if (processedIdsRef.current.has(notif.id)) continue;
      processedIdsRef.current.add(notif.id);

      const eventType = notif.notificationType;
      const config = EVENT_CONFIG[eventType];
      if (!config) continue;

      // 检查该事件类型是否启用
      if (!settings.enabledEvents[eventType]) continue;

      // 播放提示音
      if (settings.soundEnabled && audioCtxRef.current) {
        playTone(audioCtxRef.current, config.sound, settings.volume);
      }

      // 浏览器通知
      if (settings.browserNotification) {
        showBrowserNotification(
          notif.title,
          notif.content,
          `quant_${notif.id}`
        );
      }
    }

    // 更新最后检查时间
    if (latestNotifications.length > 0) {
      const latestTime = latestNotifications.reduce((max: string, n: any) => {
        const t = new Date(n.createdAt).toISOString();
        return t > max ? t : max;
      }, lastCheckTimeRef.current);
      lastCheckTimeRef.current = latestTime;
    }
  }, [latestNotifications, settings]);

  // 清理过期的已处理ID（保留最近200个）
  useEffect(() => {
    if (processedIdsRef.current.size > 200) {
      const arr = Array.from(processedIdsRef.current);
      processedIdsRef.current = new Set(arr.slice(-100));
    }
  }, [latestNotifications]);

  // 更新设置
  const updateSettings = useCallback((update: Partial<NotificationSettings>) => {
    setSettings(prev => {
      const next = { ...prev, ...update };
      saveSettings(next);
      return next;
    });
  }, []);

  // 测试提示音
  const testSound = useCallback((type: "buy" | "sell" | "warning" | "alert" | "info") => {
    initAudio();
    if (audioCtxRef.current) {
      playTone(audioCtxRef.current, type, settings.volume);
    }
  }, [settings.volume, initAudio]);

  return {
    settings,
    updateSettings,
    testSound,
    initAudio,
    unreadCount: unreadData?.count || 0,
    requestPermission: requestNotificationPermission,
  };
}
